// frontend/src/components/ManagerDashboard.js
//
// Manager's personal "My Dashboard". Managers are paid on BOOKINGS, so every GP
// and revenue figure here comes from closed-won opportunities by ACTUAL CLOSE
// DATE within the selected quarter (any quarter — no fulfillment cutover), via
// /api/payouts/team-bookings. This is intentionally a different basis from the
// rep dashboard, Team View, and Finance, which remain fulfillment-based.
//
// Manager commission = team GP × a blended rate, where the rate is the SUM of
// two components (both expressed as a % of team GP):
//
//   1. Quota attainment  — team GP vs the $1.8M quarterly quota ("New Model" rates)
//   2. GP Profit Acc      — team gross margin (team GP as a % of team revenue)
//
// Quota attainment rate (updated Aug 2026):  GP Profit Acc rate (margin):
//   110%+     → 3.25%                          26%+       → 1.25%
//   100–109%  → 2.75%                          22–25%     → 1.00%
//   75–99%    → 1.75%                          15–21%     → 0.50%
//   50–74%    → 1.25%                          below 15%  → 0.00%
//   1–49%     → 0.00%
//   (130%+ holds the top-tier 3.25% rate — confirmed by Ryan, Aug 2026.)
import React, { useState } from 'react';
import { useApi } from '../hooks/useApi';
import { getTeamBookings, getQuotas } from '../api/client';

const fmt = (n) => n == null ? '—' : new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2,
}).format(n);

// Whole-dollar formatting for large GP / revenue figures (no cents)
const fmt0 = (n) => n == null ? '—' : new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0,
}).format(n);

const pct = (n, digits = 1) => `${(n || 0).toFixed(digits)}%`;

function getCurrentQuarter() {
  const now = new Date();
  const q = Math.ceil((now.getMonth() + 1) / 3);
  return `${now.getFullYear()}-Q${q}`;
}

// ─── Manager comp plan ──────────────────────────────────────────────────────────
const DEFAULT_QUOTA_GP = 1_800_000; // fallback until the finance-configured quota loads

// Quota attainment (% of quota) → base commission rate as a % of team GP.
// Ordered high→low; each `min` is the inclusive lower bound of the band.
// The top band is open-ended so 130%+ holds the 2.50% rate.
const QUOTA_TIERS = [
  { label: '110%+',     min: 110, rate: 3.25 },
  { label: '100–109%',  min: 100, rate: 2.75 },
  { label: '75–99%',    min: 75,  rate: 1.75 },
  { label: '50–74%',    min: 50,  rate: 1.25 },
  { label: '1–49%',     min: 1,   rate: 0.00 },
];

// GP as a % of revenue (gross margin) → accelerator rate as a % of team GP.
const GP_ACC_TIERS = [
  { label: '26%+',      min: 26, rate: 1.25 },
  { label: '22–25%',    min: 22, rate: 1.00 },
  { label: '15–21%',    min: 15, rate: 0.50 },
  { label: 'Below 15%', min: 0,  rate: 0.00 },
];

// Return the first (highest) tier whose lower bound the value clears.
function resolveTier(tiers, value) {
  return tiers.find(t => value >= t.min) || null;
}

// viewer: rendered for someone looking at the Sales department from outside it
// (Operations / Finance / admins), so the heading says whose earnings these are.
export default function ManagerDashboard({ viewer = false, quarter: quarterProp, onQuarterChange } = {}) {
  // Quarter can be controlled by SalesLeaderDashboard (which swaps in the Head of
  // Sales plan from Q4 2026); standalone it keeps its own state.
  const [ownQuarter, setOwnQuarter] = useState(getCurrentQuarter());
  const quarter = quarterProp ?? ownQuarter;
  const setQuarter = onQuarterChange ?? setOwnQuarter;

  const { data: bookingData, loading: bookLoading } = useApi(() => getTeamBookings(quarter), [quarter]);
  const { data: quotaData,   loading: quotaLoading } = useApi(() => getQuotas(quarter), [quarter]);

  const loading = bookLoading || quotaLoading;
  const teamQuotaGP = quotaData?.managerPayoutQuotaGp ?? DEFAULT_QUOTA_GP; // finance-configurable

  // Booking basis: team GP + revenue from closed-won opps by actual close date
  // (all reps, all categories) for the selected quarter. See /api/payouts/team-bookings.
  const gp = bookingData?.gp || { hwSw: 0, services: 0, msa: 0, renewalMsa: 0, fod: 0 };
  const teamGP      = bookingData?.teamGP || 0;
  const teamRevenue = bookingData?.teamRevenue || 0;

  // ── Component 1: quota attainment ──
  const attainmentPct = teamQuotaGP > 0 ? (teamGP / teamQuotaGP) * 100 : 0;
  const quotaTier     = resolveTier(QUOTA_TIERS, attainmentPct);
  const quotaRate     = quotaTier?.rate || 0;

  // ── Component 2: GP margin (GP as % of revenue) ──
  const hasRevenue = teamRevenue > 0;
  const marginPct  = hasRevenue ? (teamGP / teamRevenue) * 100 : 0;
  const gpAccTier  = hasRevenue ? resolveTier(GP_ACC_TIERS, marginPct) : null;
  const gpAccRate  = gpAccTier?.rate || 0;

  // ── Blended payout ──
  const blendedRate = quotaRate + gpAccRate;          // % of team GP
  const payout      = teamGP * (blendedRate / 100);
  const qualified   = payout > 0;

  if (loading) return <DashboardSkeleton />;

  return (
    <div className="page-enter page-wrap" style={{ maxWidth: '1200px' }}>

      {/* Header */}
      <div className="page-header">
        <div>
          <div className="label" style={{ marginBottom: '6px' }}>{quarter} · {viewer ? 'Sales · Manager Commission' : 'Manager Commission'}</div>
          <h1 style={{ fontSize: '1.6rem' }}>{viewer ? 'Manager Comp' : 'My Earnings'}</h1>
        </div>
        <select
          value={quarter}
          onChange={e => setQuarter(e.target.value)}
          className="input"
          style={{ width: 'auto', minWidth: '140px' }}
        >
          {generateQuarterOptions().map(q => (
            <option key={q} value={q}>{q}</option>
          ))}
        </select>
      </div>

      {/* ── Projected Payout Hero ── */}
      <PayoutHero
        payout={payout}
        qualified={qualified}
        teamGP={teamGP}
        blendedRate={blendedRate}
        quotaRate={quotaRate}
        gpAccRate={gpAccRate}
      />

      {/* ── KPI Cards ── */}
      <div className="kpi-grid-4" style={{ gap: '12px', marginBottom: '28px' }}>
        <KpiCard label="Team QTD GP"        value={fmt0(teamGP)}                          accent="blue" />
        <KpiCard label="Quota Attainment"   value={pct(attainmentPct)}                    accent="blue"
                 sub={`of ${fmt0(teamQuotaGP)} quota`} />
        <KpiCard label="GP % of Revenue"    value={hasRevenue ? pct(marginPct) : '—'}     accent="blue"
                 sub={hasRevenue ? `on ${fmt0(teamRevenue)} rev` : 'no revenue data'} />
        <KpiCard label="Blended Rate"       value={pct(blendedRate, 2)}                   accent="green"
                 sub="of team GP" />
      </div>

      {/* ── Quota attainment ladder ── */}
      <RateLadder
        title="Quota Attainment → Commission Rate"
        headline={pct(attainmentPct)}
        subline={`${fmt0(teamGP)} of ${fmt0(teamQuotaGP)} quota`}
        tiers={QUOTA_TIERS}
        activeTier={quotaTier}
        rateLabel="of GP"
      />

      {/* ── GP Profit Acc ladder ── */}
      <RateLadder
        title="GP Profit Acc (GP % of Revenue) → Accelerator"
        headline={hasRevenue ? pct(marginPct) : '—'}
        subline={hasRevenue ? `${fmt0(teamGP)} GP on ${fmt0(teamRevenue)} revenue` : 'No revenue data for this quarter'}
        tiers={GP_ACC_TIERS}
        activeTier={gpAccTier}
        rateLabel="of GP"
      />

      {/* ── Team GP breakdown ── */}
      <GpBreakdown gp={gp} teamGP={teamGP} />

      {/* Footnote */}
      <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '18px', lineHeight: 1.5 }}>
        Managers are paid on bookings: team GP and revenue come from closed-won opportunities by actual close
        date within {quarter}. Manager commission = team GP × (quota-attainment rate + GP-margin rate). The {fmt0(teamQuotaGP)} quota
        is set by Finance (Finance → Quotas). Rates use the "New Model" schedule. Figures are projected and update
        as closed-won bookings are synced.
      </p>
    </div>
  );
}

// ─── Projected Payout Hero ──────────────────────────────────────────────────────
function PayoutHero({ payout, qualified, teamGP, blendedRate, quotaRate, gpAccRate }) {
  return (
    <div style={{
      background: qualified
        ? 'linear-gradient(135deg, var(--blue-deeper) 0%, var(--blue-dark) 100%)'
        : 'linear-gradient(135deg, var(--bg-surface) 0%, var(--bg-elevated) 100%)',
      border: `1px solid ${qualified ? 'var(--border-bright)' : 'var(--border)'}`,
      borderRadius: 'var(--radius-xl)',
      padding: '28px 36px',
      marginBottom: '28px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      boxShadow: qualified ? 'var(--shadow-glow)' : 'none',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {qualified && (
        <div style={{
          position: 'absolute', top: '-50%', right: '-10%',
          width: '300px', height: '300px', borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(0,121,194,0.2) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />
      )}

      <div>
        <div className="label" style={{ color: qualified ? 'rgba(255,255,255,0.5)' : 'var(--text-muted)', marginBottom: '8px' }}>
          Projected Manager Commission
        </div>
        <div style={{
          fontSize: '3rem', fontWeight: 700,
          fontFamily: 'var(--font-data)',
          color: qualified ? 'white' : 'var(--text-secondary)',
          letterSpacing: '-0.03em', lineHeight: 1, marginBottom: '8px',
        }}>
          {fmt(payout)}
        </div>
        <div style={{ color: qualified ? 'rgba(255,255,255,0.65)' : 'var(--text-muted)', fontSize: '14px' }}>
          {fmt0(teamGP)} team GP
          <span style={{ margin: '0 10px', opacity: 0.4 }}>×</span>
          {(blendedRate).toFixed(2)}% blended rate
        </div>
      </div>

      <div style={{ textAlign: 'right' }}>
        <div className="label" style={{ color: qualified ? 'rgba(255,255,255,0.5)' : 'var(--text-muted)', marginBottom: '6px' }}>
          Rate Build-up
        </div>
        <div style={{
          fontSize: '13px', fontFamily: 'var(--font-data)',
          color: qualified ? 'rgba(255,255,255,0.85)' : 'var(--text-secondary)', lineHeight: 1.7,
        }}>
          <div>Quota: <strong>{quotaRate.toFixed(2)}%</strong></div>
          <div>GP Acc: <strong>+{gpAccRate.toFixed(2)}%</strong></div>
          <div style={{ borderTop: `1px solid ${qualified ? 'rgba(255,255,255,0.25)' : 'var(--border)'}`, marginTop: '4px', paddingTop: '4px' }}>
            Total: <strong>{blendedRate.toFixed(2)}%</strong>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── KPI Card ─────────────────────────────────────────────────────────────────
function KpiCard({ label, value, accent, sub }) {
  const accentColor = {
    blue: 'var(--blue-primary)', green: 'var(--green)', amber: 'var(--amber)', muted: 'var(--text-muted)',
  }[accent] || 'var(--blue-primary)';

  return (
    <div className="card" style={{ padding: '18px 16px' }}>
      <div className="label" style={{ marginBottom: '10px', minHeight: '26px' }}>{label}</div>
      <div style={{
        fontSize: '1.25rem', fontWeight: 600,
        fontFamily: 'var(--font-data)', color: accentColor,
        letterSpacing: '-0.02em', whiteSpace: 'nowrap',
      }}>
        {value}
      </div>
      {sub && (
        <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px', whiteSpace: 'nowrap' }}>
          {sub}
        </div>
      )}
    </div>
  );
}

// ─── Rate Ladder (reused for both quota + GP margin) ────────────────────────────
function RateLadder({ title, headline, subline, tiers, activeTier, rateLabel }) {
  return (
    <div className="card" style={{ padding: '24px', marginBottom: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '18px' }}>
        <div>
          <div className="label" style={{ marginBottom: '4px' }}>{title}</div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}>
            <span style={{ fontSize: '1.4rem', fontWeight: 600, fontFamily: 'var(--font-data)', color: 'var(--blue-primary)' }}>
              {headline}
            </span>
            <span style={{ fontSize: '0.95rem', color: 'var(--text-secondary)', fontFamily: 'var(--font-data)' }}>
              {subline}
            </span>
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div className="label" style={{ marginBottom: '4px' }}>Current Rate</div>
          <div style={{
            fontSize: '1.1rem', fontWeight: 600, fontFamily: 'var(--font-data)',
            color: activeTier && activeTier.rate > 0 ? 'var(--green)' : 'var(--text-muted)',
          }}>
            {activeTier ? `${activeTier.rate.toFixed(2)}% ${rateLabel}` : '—'}
          </div>
        </div>
      </div>

      {/* Tier band cards (display high→low tiers left→right) */}
      <div style={{ display: 'flex', gap: '8px' }}>
        {tiers.map(t => {
          const active = activeTier?.label === t.label;
          return (
            <div key={t.label} style={{
              flex: 1, padding: '12px',
              background: active ? 'var(--blue-subtle)' : 'var(--bg-elevated)',
              border: `1px solid ${active ? 'var(--border-bright)' : 'var(--border)'}`,
              borderRadius: 'var(--radius-sm)', textAlign: 'center',
            }}>
              <div style={{
                fontSize: '15px', fontWeight: 700, fontFamily: 'var(--font-data)',
                color: active ? 'var(--blue-primary)' : 'var(--text-muted)',
              }}>
                {t.rate.toFixed(2)}%
              </div>
              <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '3px' }}>
                {t.label}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Team GP Breakdown ──────────────────────────────────────────────────────────
function GpBreakdown({ gp, teamGP }) {
  const rows = [
    { label: 'HW/SW',              value: gp.hwSw },
    { label: 'Professional Svcs',  value: gp.services },
    { label: 'Managed Svcs (MSA)', value: gp.msa },
    { label: 'Renewal MSA',        value: gp.renewalMsa },
    { label: 'FOD',                value: gp.fod },
  ];

  return (
    <div className="card" style={{ overflow: 'hidden' }}>
      <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)' }}>
        <h3>Team GP Breakdown</h3>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table className="data-table">
          <thead>
            <tr>
              <th>Category</th>
              <th style={{ textAlign: 'right' }}>QTD GP</th>
              <th style={{ textAlign: 'right' }}>% of Team GP</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.label}>
                <td style={{ fontWeight: 500, color: 'var(--text-primary)' }}>{r.label}</td>
                <td className="td-number">{fmt(r.value)}</td>
                <td className="td-number" style={{ color: 'var(--text-muted)' }}>
                  {teamGP > 0 ? pct((r.value / teamGP) * 100) : '—'}
                </td>
              </tr>
            ))}
            <tr>
              <td style={{ fontWeight: 700, color: 'var(--text-primary)' }}>Total</td>
              <td className="td-number" style={{ color: 'var(--green)', fontWeight: 700 }}>{fmt(teamGP)}</td>
              <td className="td-number" style={{ color: 'var(--text-muted)' }}>100%</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Skeleton / helpers ─────────────────────────────────────────────────────────
function DashboardSkeleton() {
  return (
    <div className="page-wrap" style={{ maxWidth: '1200px' }}>
      <div className="skeleton" style={{ height: '24px', width: '220px', marginBottom: '8px' }} />
      <div className="skeleton" style={{ height: '36px', width: '300px', marginBottom: '32px' }} />
      <div className="skeleton" style={{ height: '132px', borderRadius: '24px', marginBottom: '28px' }} />
      <div className="kpi-grid-4" style={{ gap: '12px', marginBottom: '28px' }}>
        {[1,2,3,4].map(i => <div key={i} className="skeleton" style={{ height: '90px', borderRadius: '16px' }} />)}
      </div>
      <div className="skeleton" style={{ height: '150px', borderRadius: '16px', marginBottom: '24px' }} />
      <div className="skeleton" style={{ height: '150px', borderRadius: '16px', marginBottom: '24px' }} />
      <div className="skeleton" style={{ height: '240px', borderRadius: '16px' }} />
    </div>
  );
}

function generateQuarterOptions() {
  const opts = [];
  const now = new Date();
  for (let y = now.getFullYear(); y >= now.getFullYear() - 1; y--) {
    for (let q = 4; q >= 1; q--) {
      opts.push(`${y}-Q${q}`);
    }
  }
  return opts;
}
